import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Chrome the venue nav and the venue page have to agree on: the sheets the
 * nav opens and the page renders, and small layout facts like whether the
 * page has its own category strip glued to the header.
 *
 * They have to be shared because Inertia gives a page no way to fill a named
 * slot of its layout — main.ts mounts the layout with a single default slot —
 * so the nav lives in OrderLayout while the sheets it opens live on the page.
 * Passing a callback down would mean the layout knowing what a table picker is.
 *
 * Not persisted, and reset on navigation: a sheet — or a category strip —
 * left set in this store would carry over to a page that has neither.
 */
export const useVenueChromeStore = defineStore('pjazza-venue-chrome', () => {
  const isAccountOpen = ref(false)
  const isPickerOpen = ref(false)
  // Opened from the table chip once a diner is seated: see the bill, or leave.
  const isTableMenuOpen = ref(false)
  // Opened from the dine-in join action on a printed-code venue. An overlay
  // rather than a visit to the scan page, so closing it leaves the diner on
  // the menu they were reading instead of on the directory.
  const isScanOpen = ref(false)
  // Set by Menu/Show while its category strip is actually rendered (a menu
  // with items), so PjAppHeader can drop its own hairline there and leave the
  // one at the bottom of the strip — the header's job everywhere else, since
  // most venue pages (Checkout, Tab, Book) have no strip to carry it. Menu/Show
  // resets this itself on unmount rather than closeAll doing it — that fires on
  // every way of leaving the page, including the ones closeAll below also
  // covers, so a second reset there would only be redundant.
  const hasCategoryStrip = ref(false)

  const openAccount = (): void => {
    isAccountOpen.value = true
  }

  const openPicker = (): void => {
    isPickerOpen.value = true
  }

  const openTableMenu = (): void => {
    isTableMenuOpen.value = true
  }

  const openScan = (): void => {
    isScanOpen.value = true
  }

  const closeAll = (): void => {
    isAccountOpen.value = false
    isPickerOpen.value = false
    isTableMenuOpen.value = false
    isScanOpen.value = false
  }

  return {
    isAccountOpen,
    isPickerOpen,
    isTableMenuOpen,
    isScanOpen,
    hasCategoryStrip,
    openAccount,
    openPicker,
    openTableMenu,
    openScan,
    closeAll
  }
})
